'use strict';

const libPictProvider = require('pict-provider');
const libEntityPickerSearch = require('../services/Entity-Picker-Search.js');

// The item grid is PAGINATED (server-side for the line/bid item entities; client-side for equipment, whose
// rows are derived from the join and bounded per project). PAGE_SIZE rows per page.
const PAGE_SIZE = 50;
// Equipment (ItemsViaJoin) derives its rows from ProjectEquipmentJoin; cap the join scan so a pathological
// project can't load unbounded, with an honest banner past the cap.
const VIAJOIN_MAX = 5000;
// Chunk id lists for INN filters so a project with hundreds of items never blows the URL length.
const INN_CHUNK = 100;

/**
 * ProjectContractorMatrix — a project-level grid for managing which CONTRACTORS (Organizations) are on which
 * of a project's items, over an M:N join (LineItemOrganizationJoin or BidItemOrganizationJoin). Registered as
 * TWO parameterized instances (ProjectContractorMatrixLine / ProjectContractorMatrixBid) sharing this one
 * class; each is embedded into a host div on the bespoke Project workspace via openInto('Project', id, mount)
 * — the same idiom as ProjectStaffing / RecordDates.
 *
 * Shape (items-as-rows, NOT a literal items×all-orgs matrix — a customer has thousands of orgs): each row is
 * one item, carrying toggle-chips for the project's PARTICIPATING contractors (the distinct orgs already
 * linked anywhere on the project's items + the pinned prime contractor). A chip toggles the item↔org link;
 * a small DBE pill on a linked chip toggles the join's DisadvantagedBusiness flag (LADOTD tracks DBE). A
 * per-row "+ contractor" button reveals a SERVER-SEARCHABLE Organization picker (mounted for that one row
 * only, like ProjectStaffing's "+role" — no picker storm) to add a contractor not yet participating; adding
 * one grows the chip columns. Edits stage into a per-item pending diff and commit on one Save (POST/DELETE/
 * PUT of join rows; IDCustomer is stamped by the server, never sent). A client-side row filter narrows the
 * already-loaded rows without a refetch.
 *
 * Config (from default_configuration, per instance): ItemEntity, ItemsSet, ItemIDField, ItemNumberField,
 * ItemNameField, JoinEntity, JoinsSet, JoinPKField, ProviderIdentifier, MountSelector, ItemNoun.
 */
class ProjectContractorMatrixProvider extends libPictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);
		/** @type {any} */
		this.pict;

		this._IDProject = 0;
		this._IDCustomer = 0;
		this._Mount = '';
		this._loading = false;
		this._loaded = false;
		this._saving = false;
		this._truncated = false;
		this._itemTotal = 0;
		this._message = '';
		this._messageClass = '';
		this._rowFilter = '';

		// Pagination + server-side search (line/bid page items on the server; equipment pages the derived list).
		this._page = 0;
		this._search = '';

		this._items = [];              // the CURRENT page's item rows
		this._viaJoinItems = [];       // ItemsViaJoin mode only: the full derived+filtered item list (client-paged)
		this._joinsByItem = {};        // itemId(string) -> [ { PK, IDOrganization, DBE, CreatingIDUser, IsPlaceholder } ]
		this._participating = [];      // ordered orgId(number) — prime first, then alpha by name
		this._orgLabelByID = {};       // orgId(string) -> name
		this._projectOrgIDs = [];      // the project's organization id-set (ProjectOrganizationJoin) — column + picker scope
		this._prime = 0;               // project's prime contractor IDOrganization (pinned)
		this._pending = {};            // itemId(string) -> { adds:{orgId:dbeBool}, removes:{orgId:true}, dbe:{orgId:bool} }
		this._addForItem = 0;          // the item whose inline add-picker is open (0 = none)
		this._addedItems = {};         // ItemsViaJoin mode: itemId -> { Name } for equipment staged via "+ Add equipment"
		this._removedItems = {};       // ItemsViaJoin mode: itemId -> true for equipment staged for removal from the project

		if (this.pict && this.pict.CSSMap && (typeof this.pict.CSSMap.addCSS === 'function'))
		{
			// Idempotent by key — both instances register the same block; last identical write wins.
			this.pict.CSSMap.addCSS('ProjectContractorMatrix-CSS', _CSS, 521);
		}
	}

	// --- Config accessors ---
	get cfg() { return this.options || {}; }
	get providerName() { return this.cfg.ProviderIdentifier || 'ProjectContractorMatrix'; }
	// Per-link DisadvantagedBusiness flag — true for line/bid item joins (which have the column); false for
	// ProjectEquipmentJoin (no such column). Default true so the existing instances are unchanged.
	get supportsDBE() { return this.cfg.SupportsDBE !== false; }
	// Equipment mode: rows are DERIVED from the join (ProjectEquipmentJoin), not queried from a project-owned
	// item entity (Equipment is a shared master list with no IDProject). Also drives the "+ Add equipment" control.
	get itemsViaJoin() { return this.cfg.ItemsViaJoin === true; }
	// When set (e.g. 'IDProject'), the join carries the project id: filter the join load by it and stamp it on POST.
	get joinProjectField() { return this.cfg.JoinProjectField || ''; }
	// The master-list entity/set the "+ Add <item>" picker searches (ItemsViaJoin mode only).
	get addItemsSet() { return this.cfg.AddItemsSet || this.cfg.ItemsSet; }

	get restClient() { return (this.pict.fable && this.pict.fable.HeadlightRestClient) || this.pict.HeadlightRestClient; }
	get pickerProvider() { return this.pict.providers && this.pict.providers['Pict-Section-Picker']; }
	callerCustomer() { const tmpUser = this.pict.AppData.CurrentUser; return tmpUser ? Number(tmpUser.CustomerID || tmpUser.IDCustomer) : 0; }

	/** AppData scratch for this instance (the add-picker's bound value lives here). */
	get scratch()
	{
		const tmpKey = this.providerName;
		if (!this.pict.AppData[tmpKey]) { this.pict.AppData[tmpKey] = { AddPick: '', AddItemPick: '' }; }
		return this.pict.AppData[tmpKey];
	}

	// --- REST helpers ---
	_getJSON(pURL) { const tmpRC = this.restClient; return tmpRC ? new Promise((resolve) => tmpRC.getJSON(pURL, (pErr, pBody) => resolve(pErr ? null : pBody))) : Promise.resolve(null); }
	_postJSON(pURL, pBody) { const tmpRC = this.restClient; return tmpRC ? new Promise((resolve) => tmpRC.postJSON(pURL, pBody, (pErr, pRes) => resolve({ error: pErr, result: pRes }))) : Promise.resolve({ error: 'no client' }); }
	_putJSON(pURL, pBody) { const tmpRC = this.restClient; return tmpRC ? new Promise((resolve) => tmpRC.putJSON(pURL, pBody, (pErr, pRes) => resolve({ error: pErr, result: pRes }))) : Promise.resolve({ error: 'no client' }); }
	_deleteEntity(pEntity, pID) { const tmpRC = this.restClient; return tmpRC ? new Promise((resolve) => tmpRC.deleteEntity(pEntity, pID, (pErr, pRes) => resolve({ error: pErr, result: pRes }))) : Promise.resolve({ error: 'no client' }); }

	_esc(pValue) { return String(pValue == null ? '' : pValue).replace(/[&<>"']/g, (pChar) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[pChar])); }
	_isTruthy(pValue) { return (pValue === true) || (pValue === 1) || (pValue === '1'); }
	_chunk(pArray, pSize) { const tmpOut = []; for (let i = 0; i < pArray.length; i += pSize) { tmpOut.push(pArray.slice(i, i + pSize)); } return tmpOut; }

	_canEdit()
	{
		try
		{
			const tmpApp = this.pict.fable;   // the EffectivePermissionsService lives on fable, not the app instance
			if (tmpApp && tmpApp.EffectivePermissionsService && (typeof tmpApp.EffectivePermissionsService.can === 'function'))
			{
				// Gate on the ITEM entity's Update (the proven LineItemBidItemMap gate; the join entity's
				// client-side permission set is less certain), unless GateEntity overrides it — equipment gates
				// on Project-Update, since its link is a project edit, not an edit of the shared Equipment master
				// list. The server still enforces join-level auth on each POST/DELETE at save time.
				return tmpApp.EffectivePermissionsService.can(this.cfg.GateEntity || this.cfg.ItemEntity, 'Update');
			}
		}
		catch (pError) { /* fall through */ }
		return true;
	}

	// --- Open (workspace embed) ---

	/**
	 * Render (and, on a project change, load) the grid into a host element. Safe to call on every workspace
	 * render — reloads only when the project changes; otherwise repaints from cache (the host is torn down
	 * on each whole-view render).
	 * @param {string} pMode  ignored ('Project' — matches the ProjectStaffing/RecordDates openInto signature)
	 * @param {number|string} pID
	 * @param {string} pMountSelector
	 */
	openInto(pMode, pID, pMountSelector)
	{
		const tmpID = Number(pID) || 0;
		this._Mount = pMountSelector || this.cfg.MountSelector;
		if (!tmpID) { return; }
		if (tmpID === this._IDProject && (this._loaded || this._loading))
		{
			this._render();
			return;
		}
		this._IDProject = tmpID;
		this._pending = {};
		this._addedItems = {};
		this._removedItems = {};
		this._addForItem = 0;
		this._rowFilter = '';
		this._page = 0;
		this._search = '';
		this._loaded = false;
		this._loading = true;
		this._message = '';
		this.scratch.AddPick = '';
		this._render();
		this._loadAsync(tmpID).catch((pError) => { this.pict.log.warn(`${this.providerName} load failed: ${pError && (pError.message || pError)}`); this._loading = false; this._loaded = true; this._render(); });
	}

	async _loadAsync(pIDProject)
	{
		const tmpProject = await this._getJSON(`Project/${pIDProject}`);
		this._IDCustomer = (tmpProject && Number(tmpProject.IDCustomer)) || this.callerCustomer();
		this._prime = (tmpProject && Number(tmpProject.IDOrganization)) || 0;

		// The project's organization set — the CONTRACTOR columns (stable across pages) and the add-picker's
		// candidate scope (issue #2: never the whole customer org list). Loaded once per project open.
		await this._loadProjectOrgsAsync(pIDProject);

		if (this.itemsViaJoin)
		{
			await this._loadViaJoinAsync(pIDProject);
		}
		else
		{
			await this._loadItemPageAsync();
		}

		this._loading = false;
		this._loaded = true;
		this._render();
	}

	/** Load the project's organization id-set (ProjectOrganizationJoin) + labels — the contractor column scope. */
	async _loadProjectOrgsAsync(pIDProject)
	{
		// ProjectOrganizationJoin is HARD-delete (no Deleted column — a ~FBV~Deleted~EQ~0 clause errors and drops
		// the whole result), so filter by project only.
		const tmpRows = await this._getJSON(`ProjectOrganizationJoins/FilteredTo/FBV~IDProject~EQ~${pIDProject}/0/2000`);
		const tmpIDs = new Set();
		(Array.isArray(tmpRows) ? tmpRows : []).forEach((pJoin) => { const tmpID = Number(pJoin.IDOrganization); if (tmpID > 0) { tmpIDs.add(tmpID); } });
		if (this._prime > 0) { tmpIDs.add(this._prime); }
		this._projectOrgIDs = Array.from(tmpIDs);
		await this._resolveOrgLabels(this._projectOrgIDs);
	}

	/** Server-side item page (line/bid): the current page's rows + true total, honoring the search term. */
	async _loadItemPageAsync()
	{
		const tmpSortField = this.cfg.SortField || this.cfg.ItemNumberField || 'Name';
		const tmpSearch = this._search.trim();
		const tmpSearchClause = tmpSearch ? `~FBV~${this.cfg.ItemNameField || 'Name'}~LK~${encodeURIComponent(`%${tmpSearch}%`)}` : '';
		const tmpBaseFilter = `FBV~IDProject~EQ~${this._IDProject}~FBV~Deleted~EQ~0${tmpSearchClause}`;
		const tmpStart = this._page * PAGE_SIZE;
		const [ tmpItems, tmpCount ] = await Promise.all(
			[
				this._getJSON(`${this.cfg.ItemsSet}/FilteredTo/${tmpBaseFilter}~FSF~${tmpSortField}~ASC~0/${tmpStart}/${PAGE_SIZE}`),
				this._getJSON(`${this.cfg.ItemsSet}/Count/FilteredTo/${tmpBaseFilter}`),
			]);
		this._items = Array.isArray(tmpItems) ? tmpItems : [];
		this._itemTotal = (tmpCount && (tmpCount.Count != null)) ? Number(tmpCount.Count) : this._items.length;
		this._truncated = false;
		await this._loadJoinsAsync();
	}

	/**
	 * Equipment (ItemsViaJoin): the project's equipment is defined ONLY by ProjectEquipmentJoin rows (Equipment is
	 * a shared master list with no IDProject). Load ALL the project's join rows once (filtered by JoinProjectField),
	 * bucket them, derive the distinct item list + names, then page + filter that list client-side.
	 */
	async _loadViaJoinAsync(pIDProject)
	{
		this._joinsByItem = {};
		const tmpField = this.joinProjectField || 'IDProject';
		let tmpAll = [];
		for (let tmpStart = 0; tmpStart < VIAJOIN_MAX; tmpStart += 1000)
		{
			/* eslint-disable-next-line no-await-in-loop */
			const tmpRows = await this._getJSON(`${this.cfg.JoinsSet}/FilteredTo/FBV~${tmpField}~EQ~${pIDProject}/${tmpStart}/1000`);
			if (!Array.isArray(tmpRows) || tmpRows.length === 0) { break; }
			tmpAll = tmpAll.concat(tmpRows);
			if (tmpRows.length < 1000) { break; }
		}
		this._truncated = (tmpAll.length >= VIAJOIN_MAX);
		// Bucket joins by item; an IDOrganization=0 row is a "on the project, unattributed" PLACEHOLDER, not a link.
		const tmpItemIDs = new Set();
		const tmpOrgIDs = new Set(this._projectOrgIDs);
		tmpAll.forEach((pJoin) =>
		{
			const tmpItemID = Number(pJoin[this.cfg.ItemIDField]);
			if (!(tmpItemID > 0)) { return; }
			tmpItemIDs.add(tmpItemID);
			const tmpKey = String(tmpItemID);
			if (!this._joinsByItem[tmpKey]) { this._joinsByItem[tmpKey] = []; }
			const tmpOrg = Number(pJoin.IDOrganization) || 0;
			this._joinsByItem[tmpKey].push({ PK: Number(pJoin[this.cfg.JoinPKField]), IDOrganization: tmpOrg, DBE: false, IsPlaceholder: (tmpOrg === 0) });
			if (tmpOrg > 0) { tmpOrgIDs.add(tmpOrg); }
		});
		// Resolve item (equipment) names + GUIDs via the master-list set, chunked INN.
		const tmpRecords = await this._resolveItemRecords(Array.from(tmpItemIDs));
		// Build the master row list, DEDUPED BY GUID: a customer can carry duplicate equipment records that share a
		// GUID (e.g. a re-import — "Air Compressor" as both IDEquipment 2 and 4016, GUID LADOTD-EQP-A15), and a project
		// can hold join rows to BOTH, so the raw list shows the same equipment twice. Collapse same-GUID records into
		// ONE row, merging their join buckets under a canonical id (the one with the most links; tiebreak lowest id).
		// Records with no GUID stay distinct. Line/bid items don't set ItemGUIDField, so they're never deduped.
		this._viaJoinMaster = [];
		this._projectItemGUIDs = {};
		const fLinkCount = (pID) => (this._joinsByItem[String(pID)] || []).length;
		const fMaster = (pCanonID, pAliasIDs, pGUID) =>
		{
			const tmpKey = String(pCanonID);
			if (!this._joinsByItem[tmpKey]) { this._joinsByItem[tmpKey] = []; }
			pAliasIDs.forEach((pAlias) =>
			{
				if (Number(pAlias) === Number(pCanonID)) { return; }
				(this._joinsByItem[String(pAlias)] || []).forEach((pLink) => this._joinsByItem[tmpKey].push(pLink));
				delete this._joinsByItem[String(pAlias)];
			});
			if (pGUID) { this._projectItemGUIDs[pGUID] = pCanonID; }
			this._viaJoinMaster.push({ [this.cfg.ItemIDField]: pCanonID, [this.cfg.ItemNameField]: (tmpRecords[tmpKey] && tmpRecords[tmpKey].Name) || `${this.cfg.ItemNoun} ${pCanonID}` });
		};
		if (this.cfg.ItemGUIDField)
		{
			const tmpByGUID = {};
			const tmpNoGuid = [];
			Array.from(tmpItemIDs).forEach((pID) =>
			{
				const tmpGUID = (tmpRecords[String(pID)] && tmpRecords[String(pID)].GUID) || '';
				if (tmpGUID) { (tmpByGUID[tmpGUID] = tmpByGUID[tmpGUID] || []).push(pID); }
				else { tmpNoGuid.push(pID); }
			});
			Object.keys(tmpByGUID).forEach((pGUID) =>
			{
				const tmpIDs = tmpByGUID[pGUID];
				const tmpCanon = tmpIDs.slice().sort((pA, pB) => (fLinkCount(pB) - fLinkCount(pA)) || (pA - pB))[0];
				fMaster(tmpCanon, tmpIDs, pGUID);
			});
			tmpNoGuid.forEach((pID) => fMaster(pID, [ pID ], ''));
		}
		else
		{
			Array.from(tmpItemIDs).forEach((pID) => fMaster(pID, [ pID ], ''));
		}
		await this._resolveOrgLabels(Array.from(tmpOrgIDs));
		this._rebuildParticipating();
		this._rebuildViaJoinPage();
	}

	/** Resolve item (equipment) name + GUID by INN over the master-list set. */
	async _resolveItemRecords(pItemIDs)
	{
		const tmpMap = {};
		const tmpGUIDField = this.cfg.ItemGUIDField;
		const tmpUnique = Array.from(new Set((pItemIDs || []).map(Number).filter((pID) => pID > 0)));
		const tmpChunks = this._chunk(tmpUnique, INN_CHUNK);
		const tmpResults = await Promise.all(tmpChunks.map((pChunk) =>
			this._getJSON(`${this.cfg.ItemsSet}/FilteredTo/FBL~${this.cfg.ItemIDField}~INN~${pChunk.join(',')}~FBV~Deleted~EQ~0/0/${INN_CHUNK}`)));
		tmpResults.forEach((pRows) => (Array.isArray(pRows) ? pRows : []).forEach((pRow) =>
		{
			if (pRow && (pRow[this.cfg.ItemIDField] != null))
			{
				tmpMap[String(pRow[this.cfg.ItemIDField])] = { Name: pRow[this.cfg.ItemNameField] || pRow.Name || '', GUID: tmpGUIDField ? (pRow[tmpGUIDField] || '') : '' };
			}
		}));
		return tmpMap;
	}

	/** ItemsViaJoin: (re)compute the current page of the derived item list, applying the client-side search + staged adds/removes. */
	_rebuildViaJoinPage()
	{
		const tmpQuery = this._search.trim().toLowerCase();
		let tmpAll = (this._viaJoinMaster || []).slice();
		// Fold in equipment staged via "+ Add equipment" (not yet saved) and drop equipment staged for removal.
		Object.keys(this._addedItems).forEach((pID) =>
		{
			if (!tmpAll.some((pItem) => String(pItem[this.cfg.ItemIDField]) === String(pID)))
			{
				tmpAll.push({ [this.cfg.ItemIDField]: Number(pID), [this.cfg.ItemNameField]: this._addedItems[pID].Name });
			}
		});
		tmpAll = tmpAll.filter((pItem) => !this._removedItems[String(pItem[this.cfg.ItemIDField])]);
		tmpAll.sort((pA, pB) => String(pA[this.cfg.ItemNameField] || '').localeCompare(String(pB[this.cfg.ItemNameField] || '')));
		if (tmpQuery) { tmpAll = tmpAll.filter((pItem) => String(pItem[this.cfg.ItemNameField] || '').toLowerCase().indexOf(tmpQuery) >= 0); }
		this._itemTotal = tmpAll.length;
		this._viaJoinItems = tmpAll;
		const tmpStart = this._page * PAGE_SIZE;
		this._items = tmpAll.slice(tmpStart, tmpStart + PAGE_SIZE);
	}

	/** (Re)load the join rows for the loaded items, rebuild the participating-contractor set + org labels. */
	async _loadJoinsAsync()
	{
		const tmpItemIDs = this._items.map((pItem) => Number(pItem[this.cfg.ItemIDField])).filter((pV) => pV > 0);
		this._joinsByItem = {};
		let tmpAllJoins = [];
		if (tmpItemIDs.length > 0)
		{
			// NOTE: these join tables (LineItemOrganizationJoin / BidItemOrganizationJoin) are HARD-delete —
			// they have no Deleted column, so a ~FBV~Deleted~EQ~0 clause errors ("Unknown column") and drops
			// the whole result. Filter by item id only. (A unique key AK_<Join>(item,org) means link/unlink is
			// create/hard-delete, with no soft-deleted row to revive.)
			const tmpChunks = this._chunk(tmpItemIDs, INN_CHUNK);
			const tmpResults = await Promise.all(tmpChunks.map((pChunk) =>
				this._getJSON(`${this.cfg.JoinsSet}/FilteredTo/FBL~${this.cfg.ItemIDField}~INN~${pChunk.join(',')}/0/${INN_CHUNK * 50}`)));
			tmpResults.forEach((pRows) => { if (Array.isArray(pRows)) { tmpAllJoins = tmpAllJoins.concat(pRows); } });
		}
		tmpAllJoins.forEach((pJoin) =>
		{
			const tmpItemKey = String(pJoin[this.cfg.ItemIDField]);
			if (!this._joinsByItem[tmpItemKey]) { this._joinsByItem[tmpItemKey] = []; }
			this._joinsByItem[tmpItemKey].push(
				{
					PK: Number(pJoin[this.cfg.JoinPKField]),
					IDOrganization: Number(pJoin.IDOrganization),
					DBE: this._isTruthy(pJoin.DisadvantagedBusiness),
					CreatingIDUser: Number(pJoin.CreatingIDUser) || 0,
				});
		});

		// Participating set = distinct linked orgs + the prime contractor. Load labels for all of them.
		const tmpOrgIDs = new Set();
		if (this._prime > 0) { tmpOrgIDs.add(this._prime); }
		tmpAllJoins.forEach((pJoin) => { const tmpID = Number(pJoin.IDOrganization); if (tmpID > 0) { tmpOrgIDs.add(tmpID); } });
		await this._resolveOrgLabels(Array.from(tmpOrgIDs));
		this._rebuildParticipating();
	}

	/** Batch-resolve organization names (chunked INN), merged into the label cache. */
	async _resolveOrgLabels(pOrgIDs)
	{
		const tmpMissing = (pOrgIDs || []).map(Number).filter((pID) => pID > 0 && !this._orgLabelByID[String(pID)]);
		if (tmpMissing.length === 0) { return; }
		const tmpChunks = this._chunk(tmpMissing, INN_CHUNK);
		const tmpResults = await Promise.all(tmpChunks.map((pChunk) =>
			this._getJSON(`Organizations/FilteredTo/FBL~IDOrganization~INN~${pChunk.join(',')}/0/${INN_CHUNK}`)));
		tmpResults.forEach((pRows) => (Array.isArray(pRows) ? pRows : []).forEach((pOrg) =>
		{
			if (pOrg && pOrg.IDOrganization != null) { this._orgLabelByID[String(pOrg.IDOrganization)] = pOrg.Name || `Organization ${pOrg.IDOrganization}`; }
		}));
	}

	/** Rebuild the ordered participating-contractor column set: prime first, then alpha by name. */
	_rebuildParticipating()
	{
		const tmpSet = new Set();
		if (this._prime > 0) { tmpSet.add(this._prime); }
		// The project's organizations are the stable contractor columns (project-scoped, consistent across pages).
		(this._projectOrgIDs || []).forEach((pID) => { if (pID > 0) { tmpSet.add(Number(pID)); } });
		Object.keys(this._joinsByItem).forEach((pItemKey) => this._joinsByItem[pItemKey].forEach((pJoin) => { if (pJoin.IDOrganization > 0) { tmpSet.add(pJoin.IDOrganization); } }));
		// Any orgs the user added via search this session (staged in pending adds) keep their columns too.
		Object.keys(this._pending).forEach((pItemKey) => Object.keys(this._pending[pItemKey].adds || {}).forEach((pOrg) => tmpSet.add(Number(pOrg))));
		const tmpList = Array.from(tmpSet);
		tmpList.sort((pA, pB) =>
		{
			if (pA === this._prime) { return -1; }
			if (pB === this._prime) { return 1; }
			return String(this._orgLabelByID[String(pA)] || pA).localeCompare(String(this._orgLabelByID[String(pB)] || pB));
		});
		this._participating = tmpList;
	}

	_orgLabel(pOrgID) { return this._orgLabelByID[String(pOrgID)] || `Organization ${pOrgID}`; }

	// --- Pending diff model ---

	_pendingFor(pItemID) { const tmpKey = String(pItemID); if (!this._pending[tmpKey]) { this._pending[tmpKey] = { adds: {}, removes: {}, dbe: {} }; } return this._pending[tmpKey]; }
	_cleanPending(pItemID) { const tmpKey = String(pItemID); const tmpP = this._pending[tmpKey]; if (tmpP && Object.keys(tmpP.adds).length === 0 && Object.keys(tmpP.removes).length === 0 && Object.keys(tmpP.dbe).length === 0) { delete this._pending[tmpKey]; } }
	_origJoin(pItemID, pOrgID) { return (this._joinsByItem[String(pItemID)] || []).find((pJoin) => pJoin.IDOrganization === Number(pOrgID)); }
	// ALL joins for an org on an item — a GUID-merged equipment row can carry the same org twice (once per
	// duplicate record), so a remove must delete every matching link, not just the first.
	_origJoins(pItemID, pOrgID) { return (this._joinsByItem[String(pItemID)] || []).filter((pJoin) => pJoin.IDOrganization === Number(pOrgID)); }
	_origLinked(pItemID, pOrgID) { return !!this._origJoin(pItemID, pOrgID); }
	_origDBE(pItemID, pOrgID) { const tmpJoin = this._origJoin(pItemID, pOrgID); return tmpJoin ? !!tmpJoin.DBE : false; }

	_isLinked(pItemID, pOrgID)
	{
		const tmpP = this._pending[String(pItemID)];
		const tmpOrg = String(pOrgID);
		if (tmpP)
		{
			if (tmpP.adds[tmpOrg] !== undefined) { return true; }
			if (tmpP.removes[tmpOrg]) { return false; }
		}
		return this._origLinked(pItemID, pOrgID);
	}

	_cellDBE(pItemID, pOrgID)
	{
		const tmpP = this._pending[String(pItemID)];
		const tmpOrg = String(pOrgID);
		if (tmpP && tmpP.adds[tmpOrg] !== undefined) { return !!tmpP.adds[tmpOrg]; }
		if (tmpP && tmpP.dbe[tmpOrg] !== undefined) { return !!tmpP.dbe[tmpOrg]; }
		return this._origDBE(pItemID, pOrgID);
	}

	_isStaged(pItemID, pOrgID)
	{
		const tmpP = this._pending[String(pItemID)];
		if (!tmpP) { return false; }
		const tmpOrg = String(pOrgID);
		return (tmpP.adds[tmpOrg] !== undefined) || (!!tmpP.removes[tmpOrg]) || (tmpP.dbe[tmpOrg] !== undefined);
	}

	pendingCount()
	{
		let tmpN = 0;
		Object.keys(this._pending).forEach((pKey) => { const tmpP = this._pending[pKey]; tmpN += Object.keys(tmpP.adds).length + Object.keys(tmpP.removes).length + Object.keys(tmpP.dbe).length; });
		// Equipment: staged add/remove of an item is itself a change even with no contractor toggles.
		Object.keys(this._addedItems).forEach((pKey) => { if (!this._pending[pKey]) { tmpN += 1; } });
		Object.keys(this._removedItems).forEach((pKey) => { tmpN += 1; });
		return tmpN;
	}

	/** The final set of REAL (org>0) contractors for an item after overlaying the pending diff — used for placeholder reconciliation. */
	_finalRealOrgs(pItemID)
	{
		const tmpKey = String(pItemID);
		const tmpSet = new Set((this._joinsByItem[tmpKey] || []).filter((pJoin) => pJoin.IDOrganization > 0).map((pJoin) => pJoin.IDOrganization));
		const tmpP = this._pending[tmpKey];
		if (tmpP)
		{
			Object.keys(tmpP.adds).forEach((pOrg) => tmpSet.add(Number(pOrg)));
			Object.keys(tmpP.removes).forEach((pOrg) => tmpSet.delete(Number(pOrg)));
		}
		return tmpSet;
	}

	/** The IDOrganization=0 "unattributed" placeholder join(s) for an item (a merged row may carry more than one). */
	_placeholderJoin(pItemID) { return (this._joinsByItem[String(pItemID)] || []).find((pJoin) => pJoin.IsPlaceholder); }
	_placeholderJoins(pItemID) { return (this._joinsByItem[String(pItemID)] || []).filter((pJoin) => pJoin.IsPlaceholder); }

	// --- Interactions ---

	toggleContractor(pItemID, pOrgID)
	{
		if (!this._canEdit()) { return; }
		const tmpP = this._pendingFor(pItemID);
		const tmpOrg = String(pOrgID);
		if (this._isLinked(pItemID, pOrgID))
		{
			if (tmpP.adds[tmpOrg] !== undefined) { delete tmpP.adds[tmpOrg]; }
			else { tmpP.removes[tmpOrg] = true; delete tmpP.dbe[tmpOrg]; }
		}
		else
		{
			if (tmpP.removes[tmpOrg]) { delete tmpP.removes[tmpOrg]; }
			else { tmpP.adds[tmpOrg] = false; }
		}
		this._cleanPending(pItemID);
		this._repaintRow(pItemID);
		this._syncSaveBar();
	}

	toggleDBE(pItemID, pOrgID)
	{
		if (!this._canEdit()) { return; }
		if (!this._isLinked(pItemID, pOrgID)) { return; }
		const tmpP = this._pendingFor(pItemID);
		const tmpOrg = String(pOrgID);
		if (tmpP.adds[tmpOrg] !== undefined)
		{
			tmpP.adds[tmpOrg] = !tmpP.adds[tmpOrg];
		}
		else
		{
			const tmpOrig = this._origDBE(pItemID, pOrgID);
			const tmpCur = (tmpP.dbe[tmpOrg] !== undefined) ? tmpP.dbe[tmpOrg] : tmpOrig;
			const tmpNext = !tmpCur;
			if (tmpNext === tmpOrig) { delete tmpP.dbe[tmpOrg]; }
			else { tmpP.dbe[tmpOrg] = tmpNext; }
		}
		this._cleanPending(pItemID);
		this._repaintRow(pItemID);
		this._syncSaveBar();
	}

	/** Open (or close) the inline add-a-contractor picker for one row. Only one row's picker mounts at a time. */
	toggleAddPicker(pItemID)
	{
		const tmpID = Number(pItemID) || 0;
		this._addForItem = (this._addForItem === tmpID) ? 0 : tmpID;
		this.scratch.AddPick = '';
		this._render();
	}

	/** The add-picker chose an organization: stage it as a link on the open row (grows the column set). */
	onAddPick(pOrgID)
	{
		const tmpOrg = Number(pOrgID) || 0;
		const tmpItem = this._addForItem;
		if (!tmpOrg || !tmpItem) { return; }
		// Stage the link FIRST (so _rebuildParticipating sees it in pending), unless already linked on this item.
		if (!this._isLinked(tmpItem, tmpOrg))
		{
			const tmpP = this._pendingFor(tmpItem);
			const tmpKey = String(tmpOrg);
			if (tmpP.removes[tmpKey]) { delete tmpP.removes[tmpKey]; }
			else { tmpP.adds[tmpKey] = false; }
			this._cleanPending(tmpItem);
		}
		// A newly-picked contractor becomes a participating COLUMN on every row, so a full repaint is needed
		// (not just this row). The open row's picker re-mounts afterwards because _addForItem stays set.
		this._rebuildParticipating();
		this.scratch.AddPick = '';
		this._render();
	}

	// --- Equipment (ItemsViaJoin) item add/remove ---

	/** Open/close the top-level "+ Add equipment" picker (ItemsViaJoin mode). */
	toggleAddItemPicker()
	{
		if (!this._canEdit()) { return; }
		this._addItemOpen = !this._addItemOpen;
		this.scratch.AddItemPick = '';
		this._render();
	}

	/** The add-item picker chose a piece of equipment: stage it onto the project (a row appears; it persists on save). */
	onAddItem(pItemID)
	{
		const tmpID = Number(pItemID) || 0;
		if (!tmpID) { return; }
		// Already on the project (loaded or staged)? just close.
		const tmpOnProject = (this._viaJoinMaster || []).some((pItem) => Number(pItem[this.cfg.ItemIDField]) === tmpID) || !!this._addedItems[String(tmpID)];
		if (this._removedItems[String(tmpID)]) { delete this._removedItems[String(tmpID)]; }
		if (!tmpOnProject)
		{
			this._addedItems[String(tmpID)] = { Name: (this._addItemNameByID && this._addItemNameByID[String(tmpID)]) || `${this.cfg.ItemNoun} ${tmpID}` };
		}
		this._addItemOpen = false;
		this.scratch.AddItemPick = '';
		this._page = 0;
		this._rebuildViaJoinPage();
		this._render();
	}

	/** Row × (ItemsViaJoin mode): stage this equipment for removal from the project (all its links go on save). */
	removeItem(pItemID)
	{
		if (!this._canEdit()) { return; }
		const tmpKey = String(pItemID);
		if (this._addedItems[tmpKey])
		{
			// Never-saved staged add — just drop it.
			delete this._addedItems[tmpKey];
			delete this._pending[tmpKey];
		}
		else if (this._removedItems[tmpKey])
		{
			delete this._removedItems[tmpKey];
		}
		else
		{
			this._removedItems[tmpKey] = true;
			delete this._pending[tmpKey];   // pending org edits are moot once the item leaves the project
		}
		this._rebuildViaJoinPage();
		this._render();
	}

	/** Equipment master-list search for "+ Add equipment", customer-scoped, excluding equipment already on the project. */
	_addItemDataProvider()
	{
		const tmpPageSize = 25;
		if (!this._addItemNameByID) { this._addItemNameByID = {}; }
		const fOnProject = (pID) => (this._viaJoinMaster || []).some((pItem) => Number(pItem[this.cfg.ItemIDField]) === Number(pID)) || !!this._addedItems[String(pID)];
		// Don't offer an item already on the project — by id, OR (equipment) by a GUID a duplicate record already covers.
		const fDuplicate = (pRow) => { const tmpGUID = this.cfg.ItemGUIDField ? (pRow[this.cfg.ItemGUIDField] || '') : ''; return !!(tmpGUID && this._projectItemGUIDs && this._projectItemGUIDs[tmpGUID]); };
		const fMap = (pRows) => (Array.isArray(pRows) ? pRows : [])
			.filter((pRow) => pRow && (pRow[this.cfg.ItemIDField] != null) && !fOnProject(pRow[this.cfg.ItemIDField]) && !fDuplicate(pRow))
			.map((pRow) =>
			{
				this._addItemNameByID[String(pRow[this.cfg.ItemIDField])] = pRow[this.cfg.ItemNameField] || pRow.Name || `${this.cfg.ItemNoun} ${pRow[this.cfg.ItemIDField]}`;
				return { Value: Number(pRow[this.cfg.ItemIDField]), Text: this._addItemNameByID[String(pRow[this.cfg.ItemIDField])], Record: pRow };
			});
		const tmpScope = `~FBV~IDCustomer~EQ~${this._IDCustomer || this.callerCustomer()}~FBV~Deleted~EQ~0`;
		const tmpNameField = this.cfg.ItemNameField || 'Name';
		return (pTerm, pPage) =>
		{
			const tmpTerm = String(pTerm == null ? '' : pTerm).trim();
			if (!tmpTerm)
			{
				const tmpStart = (Number(pPage) || 0) * tmpPageSize;
				return this._getJSON(`${this.addItemsSet}/FilteredTo/FBV~IDCustomer~EQ~${this._IDCustomer || this.callerCustomer()}~FBV~Deleted~EQ~0~FSF~${tmpNameField}~ASC~0/${tmpStart}/${tmpPageSize}`)
					.then((pRows) => ({ results: fMap(pRows), hasMore: (Array.isArray(pRows) && pRows.length >= tmpPageSize) }));
			}
			return this._getJSON(`${this.addItemsSet}/FilteredTo/FBV~${tmpNameField}~LK~${encodeURIComponent(`%${tmpTerm}%`)}${tmpScope}/0/${tmpPageSize}`)
				.then((pRows) => ({ results: fMap(pRows), hasMore: false }));
		};
	}

	_addItemResolveValue()
	{
		return (pValue) =>
		{
			const tmpID = Number(pValue);
			if (!tmpID) { return Promise.resolve(null); }
			const tmpName = this._addItemNameByID && this._addItemNameByID[String(tmpID)];
			return Promise.resolve({ Value: tmpID, Text: tmpName || `#${tmpID}` });
		};
	}

	// --- Search + pagination ---

	/** Debounced search: reset to page 0 and reload the item page (server-side for line/bid, client-side for equipment). */
	setSearch(pValue)
	{
		this._search = String(pValue == null ? '' : pValue);
		if (this._searchTimer) { clearTimeout(this._searchTimer); }
		this._searchTimer = setTimeout(() => { this._searchTimer = null; this._page = 0; this._reloadPage(); }, 300);
	}

	_pageCount() { return Math.max(1, Math.ceil((this._itemTotal || 0) / PAGE_SIZE)); }

	goToPage(pPage)
	{
		const tmpPage = Math.max(0, Math.min(this._pageCount() - 1, Number(pPage) || 0));
		if (tmpPage === this._page) { return; }
		this._page = tmpPage;
		this._reloadPage();
	}

	/** Reload just the current page (equipment re-slices the in-memory list; line/bid re-queries the server). */
	async _reloadPage()
	{
		if (this.itemsViaJoin)
		{
			this._rebuildViaJoinPage();
			this._renderResults();
			return;
		}
		this._loading = true;
		this._renderResults();
		try { await this._loadItemPageAsync(); }
		catch (pError) { this.pict.log.warn(`${this.providerName} page load failed: ${pError && (pError.message || pError)}`); }
		this._loading = false;
		this._renderResults();
	}

	// --- Save ---

	async saveAsync()
	{
		if (!this._canEdit()) { this._flash('You do not have permission to manage contractor links.', 'is-danger'); this._syncSaveBar(); return; }
		const tmpItemKeys = Array.from(new Set([].concat(Object.keys(this._pending), Object.keys(this._addedItems), Object.keys(this._removedItems))));
		if (tmpItemKeys.length === 0 || this._saving) { return; }

		this._saving = true;
		this._syncSaveBar();

		let tmpOK = 0;
		let tmpFail = 0;
		const fCount = (pRes) => { if (pRes && !pRes.error) { tmpOK++; } else { tmpFail++; } };
		for (let i = 0; i < tmpItemKeys.length; i++)
		{
			const tmpItemKey = tmpItemKeys[i];
			const tmpItemID = Number(tmpItemKey);

			// Equipment removed from the project — hard-delete every one of its join rows (real + placeholder).
			if (this.itemsViaJoin && this._removedItems[tmpItemKey])
			{
				for (const tmpJoin of (this._joinsByItem[tmpItemKey] || [])) { fCount(await this._deleteEntity(this.cfg.JoinEntity, tmpJoin.PK)); }
				continue;
			}

			const tmpP = this._pending[tmpItemKey] || { adds: {}, removes: {}, dbe: {} };

			// Adds — POST a new join (skip if the join already exists on the server, guarding double-submit).
			for (const tmpOrg of Object.keys(tmpP.adds))
			{
				if (this._origLinked(tmpItemID, tmpOrg)) { continue; }
				const tmpBody = { [this.cfg.ItemIDField]: tmpItemID, IDOrganization: Number(tmpOrg) };
				if (this.joinProjectField) { tmpBody[this.joinProjectField] = this._IDProject; }
				if (this.supportsDBE) { tmpBody.DisadvantagedBusiness = tmpP.adds[tmpOrg] ? 1 : 0; }
				const tmpRes = await this._postJSON(this.cfg.JoinEntity, tmpBody);
				if (tmpRes && !tmpRes.error && tmpRes.result && tmpRes.result[this.cfg.JoinPKField]) { tmpOK++; } else { tmpFail++; }
			}
			// Removes — DELETE every existing join for the org by PK (a GUID-merged row may hold more than one).
			for (const tmpOrg of Object.keys(tmpP.removes))
			{
				for (const tmpJoin of this._origJoins(tmpItemID, tmpOrg))
				{
					fCount(await this._deleteEntity(this.cfg.JoinEntity, tmpJoin.PK));
				}
			}
			// DBE changes on existing joins — PUT the flag (only where the join carries the column).
			if (this.supportsDBE)
			{
				for (const tmpOrg of Object.keys(tmpP.dbe))
				{
					const tmpJoin = this._origJoin(tmpItemID, tmpOrg);
					if (!tmpJoin) { continue; }
					fCount(await this._putJSON(this.cfg.JoinEntity, { [this.cfg.JoinPKField]: tmpJoin.PK, DisadvantagedBusiness: tmpP.dbe[tmpOrg] ? 1 : 0 }));
				}
			}
			// Equipment: keep the item ON the project with an IDOrganization=0 placeholder when it ends with no real
			// contractor, and clean the placeholder up once it has one. (Fieldbook drops org=0 rows, but the row keeps
			// the equipment attached to the project.)
			if (this.itemsViaJoin)
			{
				const tmpFinal = this._finalRealOrgs(tmpItemID);
				const tmpPlaceholder = this._placeholderJoin(tmpItemID);
				if (tmpFinal.size === 0 && !tmpPlaceholder)
				{
					const tmpBody = { [this.cfg.ItemIDField]: tmpItemID, IDOrganization: 0 };
					if (this.joinProjectField) { tmpBody[this.joinProjectField] = this._IDProject; }
					const tmpRes = await this._postJSON(this.cfg.JoinEntity, tmpBody);
					if (tmpRes && !tmpRes.error && tmpRes.result && tmpRes.result[this.cfg.JoinPKField]) { tmpOK++; } else { tmpFail++; }
				}
				else if (tmpFinal.size > 0 && tmpPlaceholder)
				{
					for (const tmpPH of this._placeholderJoins(tmpItemID))
					{
						fCount(await this._deleteEntity(this.cfg.JoinEntity, tmpPH.PK));
					}
				}
			}
		}

		// Refresh from the server so the grid reflects committed state, then clear the diff.
		if (this.itemsViaJoin) { await this._loadViaJoinAsync(this._IDProject); }
		else { await this._loadJoinsAsync(); }
		this._pending = {};
		this._addedItems = {};
		this._removedItems = {};
		this._saving = false;
		const tmpNote = tmpFail ? `, ${tmpFail} failed (a link may already exist, or you may lack permission)` : '';
		this._flash(`Saved ${tmpOK} change${tmpOK === 1 ? '' : 's'}${tmpNote}.`, tmpFail ? 'is-warning' : 'is-success');
		this._render();
	}

	_flash(pMessage, pClass) { this._message = pMessage; this._messageClass = pClass || 'is-info'; }

	// --- Render ---

	_syncSaveBar()
	{
		const tmpCA = this.pict.ContentAssignment;
		if (!tmpCA || !this._Mount) { return; }
		if (tmpCA.getElement(`${this._Mount} .pcm-savebar`).length === 0) { return; }
		tmpCA.assignContent(`${this._Mount} .pcm-savebar`, this._saveBarHTML());
	}

	_saveBarHTML()
	{
		const tmpCount = this.pendingCount();
		const tmpDisabled = (tmpCount === 0 || this._saving || !this._canEdit()) ? 'disabled' : '';
		const tmpLabel = this._saving ? 'Saving…' : (tmpCount > 0 ? `Save ${tmpCount} change${tmpCount === 1 ? '' : 's'}` : 'No changes');
		return /*html*/`
			<span class="pcm-pending">${tmpCount > 0 ? `${tmpCount} unsaved change${tmpCount === 1 ? '' : 's'}` : 'All contractor links saved'}</span>
			<button class="pcm-btn pcm-btn-primary" ${tmpDisabled} onclick="_Pict.providers.${this.providerName}.saveAsync()">${tmpLabel}</button>`;
	}

	/** Repaint just one row's chips cell (keeps the rest of the grid + any open picker intact). */
	_repaintRow(pItemID)
	{
		const tmpCA = this.pict.ContentAssignment;
		if (!tmpCA || !this._Mount) { return; }
		const tmpSel = `${this._Mount} tr[data-item="${pItemID}"] .pcm-chips`;
		if (tmpCA.getElement(tmpSel).length === 0) { return; }
		tmpCA.assignContent(tmpSel, this._chipsHTML(pItemID));
		const tmpRowEl = tmpCA.getElement(`${this._Mount} tr[data-item="${pItemID}"]`);
		if (tmpRowEl && tmpRowEl.length && tmpRowEl[0].classList) { tmpRowEl[0].classList.toggle('pcm-row-changed', !!this._pending[String(pItemID)]); }
	}

	_chipsHTML(pItemID)
	{
		const tmpEditable = this._canEdit();
		if (this._participating.length === 0)
		{
			return `<span class="pcm-empty-chips">No contractors on this project yet — use “+ contractor”.</span>`;
		}
		return this._participating.map((pOrgID) =>
		{
			const tmpLinked = this._isLinked(pItemID, pOrgID);
			const tmpStaged = this._isStaged(pItemID, pOrgID);
			const tmpName = this._esc(this._orgLabel(pOrgID));
			const tmpPrime = (pOrgID === this._prime) ? '<span class="pcm-prime" title="Project prime contractor">★</span>' : '';
			if (!tmpLinked)
			{
				const tmpClick = tmpEditable ? `onclick="_Pict.providers.${this.providerName}.toggleContractor(${pItemID}, ${pOrgID})"` : '';
				return `<span class="pcm-chip pcm-off${tmpStaged ? ' pcm-staged' : ''}" ${tmpClick} title="Link this contractor">${tmpPrime}${tmpName}</span>`;
			}
			const tmpDBE = this._cellDBE(pItemID, pOrgID);
			const tmpDBEBtn = !this.supportsDBE ? ''
				: (tmpEditable
					? `<button class="pcm-dbe${tmpDBE ? ' pcm-dbe-on' : ''}" onclick="event.stopPropagation(); _Pict.providers.${this.providerName}.toggleDBE(${pItemID}, ${pOrgID})" title="Disadvantaged Business Enterprise — click to toggle">DBE</button>`
					: (tmpDBE ? '<span class="pcm-dbe pcm-dbe-on pcm-dbe-ro" title="Disadvantaged Business Enterprise">DBE</span>' : ''));
			const tmpX = tmpEditable ? `<button class="pcm-x" onclick="event.stopPropagation(); _Pict.providers.${this.providerName}.toggleContractor(${pItemID}, ${pOrgID})" title="Unlink this contractor">×</button>` : '';
			return `<span class="pcm-chip pcm-on${tmpStaged ? ' pcm-staged' : ''}">${tmpPrime}<span class="pcm-chip-name">${tmpName}</span>${tmpDBEBtn}${tmpX}</span>`;
		}).join('');
	}

	_rowHTML(pItem)
	{
		const tmpItemID = Number(pItem[this.cfg.ItemIDField]);
		const tmpNum = this._esc(pItem[this.cfg.ItemNumberField] || pItem.ItemCode || '');
		const tmpName = this._esc(pItem[this.cfg.ItemNameField] || pItem.Description || `${this.cfg.ItemNoun} ${tmpItemID}`);
		const tmpKey = String(tmpItemID);
		const tmpStagedItem = this._addedItems[tmpKey] || this._removedItems[tmpKey];
		const tmpChanged = (this._pending[tmpKey] || tmpStagedItem) ? ' pcm-row-changed' : '';
		const tmpFilterText = this._esc(`${tmpNum} ${tmpName}`.toLowerCase());
		const tmpEditable = this._canEdit();
		const tmpAdd = tmpEditable
			? (this._addForItem === tmpItemID
				? `<span class="pcm-addwrap"><span id="pcm-addpick-${this.providerName}" class="pcm-addpick"></span><button class="pcm-btn pcm-btn-sm" onclick="_Pict.providers.${this.providerName}.toggleAddPicker(${tmpItemID})">Done</button></span>`
				: `<button class="pcm-btn pcm-btn-sm pcm-addbtn" onclick="_Pict.providers.${this.providerName}.toggleAddPicker(${tmpItemID})">+ contractor</button>`)
			: '';
		// Equipment mode: a name-cell badge for staged add / pending removal + a row × to take the equipment off the project.
		const tmpBadge = this._removedItems[tmpKey] ? '<span class="pcm-itembadge pcm-itembadge-rm">removing</span>'
			: (this._addedItems[tmpKey] ? '<span class="pcm-itembadge pcm-itembadge-new">new</span>' : '');
		const tmpRemove = (this.itemsViaJoin && tmpEditable)
			? `<button class="pcm-itemx" title="${this._removedItems[tmpKey] ? 'Keep this equipment on the project' : 'Remove this equipment from the project'}" onclick="_Pict.providers.${this.providerName}.removeItem(${tmpItemID})">${this._removedItems[tmpKey] ? '↺' : '×'}</button>`
			: '';
		return /*html*/`
			<tr class="pcm-row${tmpChanged}" data-item="${tmpItemID}" data-filter="${tmpFilterText}">
				<td class="pcm-num">${tmpNum}</td>
				<td class="pcm-name">${tmpName}${tmpBadge}${tmpRemove}</td>
				<td class="pcm-cell"><span class="pcm-chips">${this._chipsHTML(tmpItemID)}</span><span class="pcm-add">${tmpAdd}</span></td>
			</tr>`;
	}

	/** Pagination + "+ Add <item>" toolbar controls. */
	_pagerHTML()
	{
		const tmpPageCount = this._pageCount();
		const tmpFrom = this._itemTotal === 0 ? 0 : (this._page * PAGE_SIZE) + 1;
		const tmpTo = Math.min(this._itemTotal, (this._page + 1) * PAGE_SIZE);
		const tmpNoun = this._esc(this.cfg.ItemNoun.toLowerCase());
		const tmpPrevDis = (this._page <= 0) ? 'disabled' : '';
		const tmpNextDis = (this._page >= tmpPageCount - 1) ? 'disabled' : '';
		if (tmpPageCount <= 1) { return `<div class="pcm-count">${this._itemTotal.toLocaleString()} ${tmpNoun}${this._itemTotal === 1 ? '' : 's'}</div>`; }
		return /*html*/`
			<div class="pcm-pager">
				<button class="pcm-btn pcm-btn-sm" ${tmpPrevDis} onclick="_Pict.providers.${this.providerName}.goToPage(${this._page - 1})">‹ Prev</button>
				<span class="pcm-pageinfo">${tmpFrom.toLocaleString()}–${tmpTo.toLocaleString()} of ${this._itemTotal.toLocaleString()}</span>
				<button class="pcm-btn pcm-btn-sm" ${tmpNextDis} onclick="_Pict.providers.${this.providerName}.goToPage(${this._page + 1})">Next ›</button>
			</div>`;
	}

	_render()
	{
		const tmpCA = this.pict.ContentAssignment;
		if (!tmpCA || !this._Mount) { return; }
		if (tmpCA.getElement(this._Mount).length === 0) { return; }

		if (this._loading) { tmpCA.assignContent(this._Mount, `<div class="pcm-empty">Loading ${this._esc(this.cfg.ItemNoun.toLowerCase())}s…</div>`); return; }

		const tmpNoun = this._esc(this.cfg.ItemNoun.toLowerCase());
		const tmpReadOnly = !this._canEdit();
		const tmpSearching = this._search.trim() !== '';
		// A genuinely empty grid (no items, not a no-match) still needs its toolbar in equipment mode so the user can
		// add equipment; for line/bid a truly empty project shows the simple empty note.
		if (this._items.length === 0 && !tmpSearching && this._itemTotal === 0 && !this.itemsViaJoin)
		{
			tmpCA.assignContent(this._Mount, `<div class="pcm-empty">This project has no ${tmpNoun}s.</div>`); return;
		}

		const tmpMessage = this._message ? `<div class="pcm-msg ${this._messageClass}">${this._esc(this._message)}</div>` : '';
		const tmpReadNote = tmpReadOnly ? `<div class="pcm-msg is-warning">You can view contractor links here but not change them.</div>` : '';
		const tmpTrunc = this._truncated ? `<div class="pcm-msg is-warning">This project has more than ${VIAJOIN_MAX.toLocaleString()} ${tmpNoun} links; showing the first set.</div>` : '';
		const tmpRows = this._items.map((pItem) => this._rowHTML(pItem)).join('');
		const tmpEmptyRows = (this._items.length === 0)
			? `<tr><td colspan="3" class="pcm-nomatch">${tmpSearching ? `No ${tmpNoun}s match your search.` : `No ${tmpNoun}s on this project yet.`}</td></tr>`
			: '';
		const tmpSearchVal = this._esc(this._search);
		const tmpAddItem = (this.itemsViaJoin && !tmpReadOnly)
			? (this._addItemOpen
				? `<span class="pcm-additemwrap"><span id="pcm-additempick-${this.providerName}" class="pcm-additempick"></span><button class="pcm-btn pcm-btn-sm" onclick="_Pict.providers.${this.providerName}.toggleAddItemPicker()">Done</button></span>`
				: `<button class="pcm-btn pcm-btn-sm pcm-btn-primary" onclick="_Pict.providers.${this.providerName}.toggleAddItemPicker()">+ Add ${tmpNoun}</button>`)
			: '';

		tmpCA.assignContent(this._Mount, /*html*/`
			<div class="pcm">
				${tmpReadNote}${tmpTrunc}${tmpMessage}
				<div class="pcm-toolbar">
					<input class="pcm-filter" type="text" placeholder="Search ${tmpNoun}s…" value="${tmpSearchVal}" oninput="_Pict.providers.${this.providerName}.setSearch(this.value)" />
					${tmpAddItem}
					<span id="pcm-pager-${this.providerName}">${this._pagerHTML()}</span>
					<div class="pcm-savebar">${this._saveBarHTML()}</div>
				</div>
				<div class="pcm-tablewrap" id="pcm-tablewrap-${this.providerName}">
					<table class="pcm-table">
						<thead><tr><th class="pcm-th-num">#</th><th class="pcm-th-name">${this._esc(this.cfg.ItemNoun)}</th><th>Contractors</th></tr></thead>
						<tbody>${tmpRows}${tmpEmptyRows}</tbody>
					</table>
				</div>
			</div>`);

		if (this._addForItem && !tmpReadOnly) { this._mountAddPicker(); }
		if (this._addItemOpen && this.itemsViaJoin && !tmpReadOnly) { this._mountAddItemPicker(); }
	}

	/**
	 * Repaint ONLY the results (table body + pager), leaving the toolbar's search <input> untouched. A full
	 * _render() rebuilds the input and drops focus/caret while typing — and its _loading branch replaces the
	 * whole mount (input included). Used by the search/pagination reload; falls back to a full render before
	 * the shell exists. The loading indicator is routed into #pcm-tablewrap so the input survives it.
	 */
	_renderResults()
	{
		const tmpCA = this.pict.ContentAssignment;
		const tmpWrap = `#pcm-tablewrap-${this.providerName}`;
		if (!tmpCA || tmpCA.getElement(tmpWrap).length === 0) { this._render(); return; }
		const tmpNoun = this._esc(this.cfg.ItemNoun.toLowerCase());
		if (this._loading)
		{
			tmpCA.assignContent(tmpWrap, `<div class="pcm-empty">Loading ${tmpNoun}s…</div>`);
			return;
		}
		const tmpSearching = this._search.trim() !== '';
		const tmpRows = this._items.map((pItem) => this._rowHTML(pItem)).join('');
		const tmpEmptyRows = (this._items.length === 0)
			? `<tr><td colspan="3" class="pcm-nomatch">${tmpSearching ? `No ${tmpNoun}s match your search.` : `No ${tmpNoun}s on this project yet.`}</td></tr>`
			: '';
		tmpCA.assignContent(tmpWrap, /*html*/`
			<table class="pcm-table">
				<thead><tr><th class="pcm-th-num">#</th><th class="pcm-th-name">${this._esc(this.cfg.ItemNoun)}</th><th>Contractors</th></tr></thead>
				<tbody>${tmpRows}${tmpEmptyRows}</tbody>
			</table>`);
		tmpCA.assignContent(`#pcm-pager-${this.providerName}`, this._pagerHTML());
	}

	/** Mount the top-level "+ Add equipment" picker (ItemsViaJoin mode). */
	_mountAddItemPicker()
	{
		const tmpPicker = this.pickerProvider;
		const tmpCA = this.pict.ContentAssignment;
		if (!tmpPicker || (typeof tmpPicker.createPicker !== 'function') || !tmpCA) { return; }
		const tmpDest = `#pcm-additempick-${this.providerName}`;
		if (tmpCA.getElement(tmpDest).length === 0) { return; }
		const tmpInstance = tmpPicker.createPicker(`${this.providerName}-AddItemPicker`,
			{
				DestinationAddress: tmpDest,
				ValueAddress: `AppData.${this.providerName}.AddItemPick`,
				Searchable: true,
				AllowClear: true,
				ClearLabel: `— add ${this._esc(this.cfg.ItemNoun.toLowerCase())} —`,
				Placeholder: `Search ${this._esc(this.cfg.ItemNoun.toLowerCase())}…`,
				DataProvider: this._addItemDataProvider(),
				ResolveValue: this._addItemResolveValue(),
				OnChange: (pValue) => this.onAddItem(pValue),
			});
		if (tmpInstance && (typeof tmpInstance.render === 'function')) { tmpInstance.render(); }
		if (tmpInstance && (typeof tmpInstance.setValue === 'function')) { tmpInstance.setValue(''); }
	}

	// --- Add-a-contractor picker (server-side Organization search; only the open row mounts one) ---

	_mountAddPicker()
	{
		const tmpPicker = this.pickerProvider;
		const tmpCA = this.pict.ContentAssignment;
		if (!tmpPicker || (typeof tmpPicker.createPicker !== 'function') || !tmpCA) { return; }
		const tmpDest = `#pcm-addpick-${this.providerName}`;
		if (tmpCA.getElement(tmpDest).length === 0) { return; }
		const tmpInstance = tmpPicker.createPicker(`${this.providerName}-AddPicker`,
			{
				DestinationAddress: tmpDest,
				ValueAddress: `AppData.${this.providerName}.AddPick`,
				Searchable: true,
				AllowClear: true,
				ClearLabel: '— pick a contractor —',
				Placeholder: 'Search organizations…',
				DataProvider: this._orgDataProvider(),
				ResolveValue: this._orgResolveValue(),
				OnChange: (pValue) => this.onAddPick(pValue),
			});
		this._addPickerInstance = tmpInstance;
		if (tmpInstance && (typeof tmpInstance.render === 'function')) { tmpInstance.render(); }
		if (tmpInstance && (typeof tmpInstance.setValue === 'function')) { tmpInstance.setValue(''); }
	}

	/**
	 * Organization search for the add-a-contractor picker, SCOPED TO THE PROJECT'S ORGANIZATIONS (issue #2 — never
	 * the whole customer org list). The project org id-set is small (the orgs formally on the project), so the
	 * candidates are the project orgs (minus any already a column), filtered client-side by the typed term. Falls
	 * back to the customer scope only if the project has no organization set loaded.
	 */
	_orgDataProvider()
	{
		const tmpPageSize = 25;
		const fToOption = (pOrg) =>
		{
			if (pOrg && pOrg.IDOrganization != null) { this._orgLabelByID[String(pOrg.IDOrganization)] = pOrg.Name || `Organization ${pOrg.IDOrganization}`; }
			return { Value: Number(pOrg.IDOrganization), Text: pOrg.Name || `Organization ${pOrg.IDOrganization}`, Record: pOrg };
		};
		const fCustomerSearch = libEntityPickerSearch.entityPickerDataProvider((pURL) => this._getJSON(pURL),
			{ Set: 'Organizations', IDField: 'IDOrganization', PageSize: tmpPageSize, Scope: () => `FBV~IDCustomer~EQ~${this._IDCustomer || this.callerCustomer()}~FBV~Deleted~EQ~0`, ToOption: fToOption });
		return (pTerm, pPage) =>
		{
			const tmpTerm = String(pTerm == null ? '' : pTerm).trim().toLowerCase();
			const tmpIDs = (this._projectOrgIDs || []).filter((pID) => pID > 0);
			if (tmpIDs.length > 0)
			{
				// The project's organizations, resolved + filtered by term — resolve any missing labels first.
				const tmpMissing = tmpIDs.filter((pID) => !this._orgLabelByID[String(pID)]);
				const fBuild = () =>
				{
					const tmpResults = tmpIDs
						.map((pID) => ({ Value: pID, Text: this._orgLabelByID[String(pID)] || `Organization ${pID}` }))
						.filter((pRow) => !tmpTerm || String(pRow.Text).toLowerCase().indexOf(tmpTerm) >= 0)
						.sort((pA, pB) => String(pA.Text).localeCompare(String(pB.Text)));
					return { results: tmpResults, hasMore: false };
				};
				if (tmpMissing.length === 0) { return Promise.resolve(fBuild()); }
				return this._resolveOrgLabels(tmpMissing).then(fBuild);
			}
			// Fallback: customer scope (project has no org set) — keeps the picker usable.
			return fCustomerSearch(pTerm, pPage);
		};
	}

	_orgResolveValue()
	{
		return (pValue) =>
		{
			const tmpID = Number(pValue);
			if (!tmpID) { return Promise.resolve(null); }
			if (this._orgLabelByID[String(tmpID)]) { return Promise.resolve({ Value: tmpID, Text: this._orgLabelByID[String(tmpID)] }); }
			return this._getJSON(`Organization/${tmpID}`).then((pOrg) =>
			{
				const tmpName = (pOrg && pOrg.Name) ? pOrg.Name : `#${tmpID}`;
				this._orgLabelByID[String(tmpID)] = tmpName;
				return { Value: tmpID, Text: tmpName };
			});
		};
	}
}

module.exports = ProjectContractorMatrixProvider;

module.exports.default_configuration =
{
	ProviderIdentifier: 'ProjectContractorMatrix',
	AutoInitialize: true,
	AutoInitializeOrdinal: 223,
};

const _CSS = /*css*/`
	.pcm { font-size: 0.9rem; }
	.pcm-empty { padding: 1.2rem; color: var(--theme-color-text-muted,#6b7686); text-align:center; }
	.pcm-toolbar { display:flex; align-items:center; gap:0.8rem; margin:0 0 0.7rem; flex-wrap:wrap; }
	.pcm-filter { flex:1 1 14rem; min-width:10rem; max-width:22rem; font-size:0.86rem; padding:0.34rem 0.6rem; border:1px solid var(--theme-color-border-medium,#d5dbe3); border-radius:6px; background:var(--theme-color-background-input,#fff); color:var(--theme-color-text-primary,#1f2733); }
	.pcm-count { font-size:0.82rem; color:var(--theme-color-text-muted,#6b7686); }
	.pcm-savebar { display:flex; align-items:center; gap:0.7rem; margin-left:auto; }
	.pcm-pending { font-size:0.82rem; color:var(--theme-color-text-secondary,#45505f); }
	.pcm-btn { font-size:0.85rem; font-weight:600; padding:0.4rem 0.9rem; border-radius:7px; border:1px solid var(--theme-color-border-medium,#d5dbe3); background:var(--theme-color-background-panel,#fff); color:var(--theme-color-text-primary,#1f2733); cursor:pointer; }
	.pcm-btn-sm { padding:0.22rem 0.6rem; font-size:0.8rem; font-weight:600; }
	.pcm-btn-primary { color:#fff; background:var(--theme-color-primary,#2f6fed); border-color:var(--theme-color-primary,#2f6fed); }
	.pcm-btn[disabled] { opacity:0.5; cursor:default; }
	.pcm-msg { font-size:0.84rem; border-radius:6px; padding:0.45rem 0.65rem; margin:0 0 0.6rem; }
	.pcm-msg.is-info { color:#1f5673; background:var(--theme-color-status-info-bg,#e5f2f8); }
	.pcm-msg.is-success { color:#276749; background:var(--theme-color-status-success-bg,#e6f4ea); }
	.pcm-msg.is-warning { color:#8a6d1b; background:var(--theme-color-status-warning-bg,#fbf3d9); }
	.pcm-msg.is-danger { color:#a12d22; background:var(--theme-color-status-danger-bg,#fbe6e3); }
	.pcm-tablewrap { overflow-x:auto; border:1px solid var(--theme-color-border-light,#e8ebf0); border-radius:8px; }
	.pcm-table { width:100%; border-collapse:separate; border-spacing:0; }
	.pcm-table th { text-align:left; font-size:0.66rem; font-weight:700; text-transform:uppercase; letter-spacing:0.05em; color:var(--theme-color-text-muted,#6b7686); background:var(--theme-color-background-tertiary,#f4f6f9); padding:0.5rem 0.85rem; border-bottom:1px solid var(--theme-color-border-light,#e8ebf0); }
	.pcm-table td { padding:0.5rem 0.85rem; border-bottom:1px solid var(--theme-color-border-light,#eef1f5); vertical-align:top; }
	.pcm-table tbody tr:last-child td { border-bottom:none; }
	.pcm-th-num { width:1%; white-space:nowrap; }
	.pcm-th-name { width:22%; }
	.pcm-row-changed td { background:var(--theme-color-status-warning-bg,#fbf3d9); }
	.pcm-num { white-space:nowrap; color:var(--theme-color-text-muted,#6b7686); font-variant-numeric:tabular-nums; }
	.pcm-name { color:var(--theme-color-text-primary,#1f2733); }
	.pcm-cell { min-width:22rem; }
	.pcm-chips { display:flex; flex-wrap:wrap; gap:0.35rem; }
	.pcm-chip { display:inline-flex; align-items:center; gap:0.3rem; font-size:0.8rem; border-radius:999px; padding:0.12rem 0.5rem; border:1px solid var(--theme-color-border-medium,#d5dbe3); cursor:pointer; user-select:none; }
	.pcm-chip.pcm-off { color:var(--theme-color-text-muted,#6b7686); background:var(--theme-color-background-panel,#fff); border-style:dashed; }
	.pcm-chip.pcm-off:hover { color:var(--theme-color-text-secondary,#45505f); border-color:var(--theme-color-primary,#2f6fed); }
	.pcm-chip.pcm-on { color:var(--theme-color-primary-text,#1f3a6d); background:var(--theme-color-background-selected,#eef1f8); border-color:var(--theme-color-primary,#2f6fed); cursor:default; }
	.pcm-chip.pcm-staged { box-shadow:0 0 0 2px var(--theme-color-status-warning,#d9a441); }
	.pcm-chip-name { font-weight:600; }
	.pcm-prime { color:var(--theme-color-status-warning,#d9a441); font-size:0.85rem; }
	.pcm-x { border:none; background:none; cursor:pointer; color:var(--theme-color-text-muted,#6b7686); font-size:1rem; line-height:1; padding:0 0 0 0.1rem; }
	.pcm-x:hover { color:var(--theme-color-status-danger,#cf5b5b); }
	.pcm-dbe { border:1px solid var(--theme-color-border-medium,#d5dbe3); background:var(--theme-color-background-panel,#fff); color:var(--theme-color-text-muted,#6b7686); border-radius:5px; font-size:0.62rem; font-weight:700; letter-spacing:0.03em; padding:0.02rem 0.3rem; cursor:pointer; }
	.pcm-dbe.pcm-dbe-on { color:#fff; background:var(--theme-color-status-success,#3c9a5f); border-color:var(--theme-color-status-success,#3c9a5f); }
	.pcm-dbe.pcm-dbe-ro { cursor:default; }
	.pcm-add { display:inline-flex; margin-top:0.35rem; }
	.pcm-addbtn { border-style:dashed; color:var(--theme-color-text-secondary,#45505f); }
	.pcm-addwrap { display:inline-flex; align-items:center; gap:0.4rem; }
	.pcm-addpick { display:inline-block; min-width:18rem; }
	.pcm-empty-chips { font-size:0.82rem; color:var(--theme-color-text-muted,#9aa3ad); font-style:italic; }
	.pcm-nomatch { padding:0.8rem; color:var(--theme-color-text-muted,#6b7686); text-align:center; font-size:0.86rem; }
	.pcm-pager { display:flex; align-items:center; gap:0.5rem; }
	.pcm-pageinfo { font-size:0.8rem; color:var(--theme-color-text-muted,#6b7686); font-variant-numeric:tabular-nums; white-space:nowrap; }
	.pcm-itembadge { margin-left:0.5rem; font-size:0.6rem; font-weight:700; text-transform:uppercase; letter-spacing:0.03em; padding:0.05rem 0.4rem; border-radius:9px; vertical-align:middle; }
	.pcm-itembadge-new { color:#276749; background:var(--theme-color-status-success-bg,#e6f4ea); }
	.pcm-itembadge-rm { color:#a12d22; background:var(--theme-color-status-danger-bg,#fbe6e3); }
	.pcm-itemx { margin-left:0.4rem; border:none; background:none; cursor:pointer; color:var(--theme-color-text-muted,#9aa3ad); font-size:1rem; line-height:1; padding:0 0.15rem; vertical-align:middle; }
	.pcm-itemx:hover { color:var(--theme-color-status-danger,#cf5b5b); }
	.pcm-additemwrap { display:inline-flex; align-items:center; gap:0.4rem; }
	.pcm-additempick { display:inline-block; min-width:16rem; }
`;
